/**
 * Chart — a dependency-free SVG chart renderer (bar / line / area / pie).
 *
 * Pure projection of the model from web/src/lib/chart.js (buildChartModel) onto
 * a fixed virtual coordinate space, scaled to its container via the SVG viewBox
 * (so it's resolution-independent and the same render is reused for PNG export).
 * Colors come from the chart palette; text uses currentColor so it follows the
 * theme. Props: { model } (the buildChartModel output) and optional { ref } to
 * grab the <svg> for export.
 */
import { createMemo, For, Show } from 'solid-js';

const W = 960, H = 520;
const M = { top: 22, right: 150, bottom: 76, left: 70 };
const PLOT = { x: M.left, y: M.top, w: W - M.left - M.right, h: H - M.top - M.bottom };

// Compact tick label (1.2k, 3.4M) so axes stay readable.
const fmtNum = (n) => {
  const a = Math.abs(n);
  if (a >= 1e9) return (n / 1e9).toFixed(a % 1e9 ? 1 : 0) + 'B';
  if (a >= 1e6) return (n / 1e6).toFixed(a % 1e6 ? 1 : 0) + 'M';
  if (a >= 1e3) return (n / 1e3).toFixed(a % 1e3 ? 1 : 0) + 'k';
  return String(Number(n.toFixed(2)));
};
const truncate = (s, n) => (s.length > n ? s.slice(0, n - 1) + '…' : s);

export default function Chart(props) {
  const m = () => props.model;
  const yScale = (v) => {
    const { yMin, yMax } = m();
    const t = yMax === yMin ? 0 : (v - yMin) / (yMax - yMin);
    return PLOT.y + PLOT.h * (1 - t);
  };
  const y0 = createMemo(() => yScale(Math.max(0, m().yMin)));

  const catBand = createMemo(() => {
    const n = m().categories.length || 1;
    return PLOT.w / n;
  });
  const catCenter = (i) => PLOT.x + catBand() * (i + 0.5);

  // Rotate x labels when there are many / long ones.
  const rotate = createMemo(() => {
    const c = m().categories;
    return c.length > 8 || c.some((s) => s.length > 6);
  });

  return (
    <svg ref={props.ref} class="chart-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet"
         xmlns="http://www.w3.org/2000/svg">
      <Show when={m().type !== 'pie'}>
        {/* Y grid + ticks */}
        <For each={m().yTicks}>
          {(t) => (
            <g class="chart-grid">
              <line x1={PLOT.x} y1={yScale(t)} x2={PLOT.x + PLOT.w} y2={yScale(t)} />
              <text x={PLOT.x - 8} y={yScale(t) + 4} text-anchor="end" class="chart-tick">{fmtNum(t)}</text>
            </g>
          )}
        </For>
        {/* Axes */}
        <line class="chart-axis" x1={PLOT.x} y1={PLOT.y} x2={PLOT.x} y2={PLOT.y + PLOT.h} />
        <line class="chart-axis" x1={PLOT.x} y1={y0()} x2={PLOT.x + PLOT.w} y2={y0()} />

        {/* X category labels */}
        <For each={m().categories}>
          {(c, i) => (
            <text class="chart-tick" text-anchor={rotate() ? 'end' : 'middle'}
                  transform={rotate()
                    ? `translate(${catCenter(i())},${PLOT.y + PLOT.h + 14}) rotate(-35)`
                    : ''}
                  x={rotate() ? 0 : catCenter(i())}
                  y={rotate() ? 0 : PLOT.y + PLOT.h + 18}>
              {truncate(c, 14)}
            </text>
          )}
        </For>

        {/* Bars */}
        <Show when={m().type === 'bar'}>
          <For each={m().categories}>
            {(c, ci) => {
              const ns = () => m().series.length || 1;
              const gw = () => catBand() * 0.74;
              const bw = () => gw() / ns();
              const gx = () => catCenter(ci()) - gw() / 2;
              return (
                <For each={m().series}>
                  {(s, si) => {
                    const v = () => s.values[ci()] ?? 0;
                    const top = () => Math.min(yScale(v()), y0());
                    const hgt = () => Math.abs(yScale(v()) - y0());
                    return (
                      <rect class="chart-bar" x={gx() + bw() * si()} y={top()} width={Math.max(1, bw() - 1)} height={hgt()} fill={s.color}>
                        <title>{c} · {s.name}: {v()}</title>
                      </rect>
                    );
                  }}
                </For>
              );
            }}
          </For>
        </Show>

        {/* Area fills (under lines) */}
        <Show when={m().type === 'area'}>
          <For each={m().series}>
            {(s) => {
              const d = () => {
                const pts = s.values.map((v, i) => `${catCenter(i)},${yScale(v)}`);
                if (!pts.length) return '';
                return `M ${PLOT.x + catBand() * 0.5},${y0()} L ` + pts.join(' L ') +
                       ` L ${catCenter(s.values.length - 1)},${y0()} Z`;
              };
              return <path class="chart-area" d={d()} fill={s.color} />;
            }}
          </For>
        </Show>

        {/* Lines + points (line and area) */}
        <Show when={m().type === 'line' || m().type === 'area'}>
          <For each={m().series}>
            {(s) => (
              <>
                <polyline class="chart-line" fill="none" stroke={s.color}
                          points={s.values.map((v, i) => `${catCenter(i)},${yScale(v)}`).join(' ')} />
                <For each={s.values}>
                  {(v, i) => (
                    <circle class="chart-point" cx={catCenter(i())} cy={yScale(v)} r="3" fill={s.color}>
                      <title>{m().categories[i()]} · {s.name}: {v}</title>
                    </circle>
                  )}
                </For>
              </>
            )}
          </For>
        </Show>
      </Show>

      {/* Pie */}
      <Show when={m().type === 'pie'}>
        {(() => {
          const cx = PLOT.x + PLOT.w / 2, cy = PLOT.y + PLOT.h / 2;
          const r = Math.min(PLOT.w, PLOT.h) / 2 - 8;
          const vals = m().series[0]?.values || [];
          const total = m().total || vals.reduce((a, b) => a + b, 0) || 1;
          let acc = 0;
          const arcs = vals.map((v, i) => {
            const a0 = (acc / total) * Math.PI * 2 - Math.PI / 2;
            acc += v;
            const a1 = (acc / total) * Math.PI * 2 - Math.PI / 2;
            const large = a1 - a0 > Math.PI ? 1 : 0;
            const x0 = cx + r * Math.cos(a0), y0p = cy + r * Math.sin(a0);
            const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
            const mid = (a0 + a1) / 2;
            const pct = total ? Math.round((v / total) * 100) : 0;
            return {
              d: `M ${cx} ${cy} L ${x0} ${y0p} A ${r} ${r} 0 ${large} 1 ${x1} ${y1} Z`,
              color: PALETTE_AT(i), label: m().categories[i], v, pct,
              lx: cx + (r * 0.62) * Math.cos(mid), ly: cy + (r * 0.62) * Math.sin(mid),
            };
          });
          return (
            <>
              <For each={arcs}>
                {(a) => (
                  <g>
                    <path d={a.d} fill={a.color} class="chart-slice"><title>{a.label}: {a.v} ({a.pct}%)</title></path>
                    <Show when={a.pct >= 6}>
                      <text class="chart-slice-lbl" x={a.lx} y={a.ly} text-anchor="middle">{a.pct}%</text>
                    </Show>
                  </g>
                )}
              </For>
            </>
          );
        })()}
      </Show>

      {/* Legend */}
      <Show when={m().type === 'pie'} fallback={
        <For each={m().series}>
          {(s, i) => (
            <g transform={`translate(${W - M.right + 14},${M.top + 6 + i() * 20})`}>
              <rect width="11" height="11" rx="2" fill={s.color} />
              <text class="chart-legend" x="17" y="10">{truncate(s.name, 16)}</text>
            </g>
          )}
        </For>
      }>
        <For each={m().categories}>
          {(c, i) => (
            <g transform={`translate(${W - M.right + 14},${M.top + 6 + i() * 20})`}>
              <rect width="11" height="11" rx="2" fill={PALETTE_AT(i())} />
              <text class="chart-legend" x="17" y="10">{truncate(c, 16)}</text>
            </g>
          )}
        </For>
      </Show>
    </svg>
  );
}

// Palette accessor (kept local so the renderer doesn't need the lib import just
// for colors; mirrors lib/chart.js PALETTE order for pie slices/legend).
const PALETTE = [
  '#0ea5e9', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6',
  '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16',
  '#06b6d4', '#e11d48',
];
const PALETTE_AT = (i) => PALETTE[i % PALETTE.length];
